'use client';

import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import PerfectFilter from '@/components/PerfectFilter';
import ShopElement1 from '@/components/ShopElement1';
import type { BrandGroup } from '@/lib/api';
import FAQSection from '@/components/FAQSection';
import Gallery from '@/components/Gallery';
import { galleryImages } from '@/data/galleryImages';
import { shopByVehicleFaqs } from '@/data/faqData';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';

const WHY_CHOOSE_ITEMS = [
  {
    title: 'Washable & Reusable',
    text: 'Green Filter air filters are designed to be cleaned, serviced, and reused instead of routinely discarded and replaced like traditional disposable filters.',
  },
  {
    title: 'High-Flow Filter Design',
    text: "Green Filter's filter media is engineered to support airflow while helping trap dirt and contaminants before they enter the engine.",
  },
  {
    title: 'Filtration Down to 5 Microns',
    text: 'Green Filter air filters are designed to capture particles as small as 5 microns while maintaining airflow through the filter media.',
  },
  {
    title: 'Made in the USA',
    text: "Green High Performance Air Filters are made by hand at Green Filter's facility in Mount Braddock, Pennsylvania, using materials manufactured in the USA.",
  },
  {
    title: 'Designed for Long-Term Use',
    text: "With proper cleaning and maintenance, Green Filter reusable air filters are built for long-term service and backed by Green Filter's Limited Lifetime Warranty.",
  },
  {
    title: 'Vehicle-Specific Fitment',
    text: 'Search by your exact year, make, model, and engine to identify compatible applications before ordering.',
  },
];

const HOW_TO_STEPS = [
  'Select your vehicle year.',
  'Choose the make.',
  'Select the model.',
  'Choose the correct engine.',
  'Review the matching Green Filter applications.',
  'Confirm the vehicle and product details before ordering.',
];

interface ShopByVehiclePageProps {
  brandGroups: BrandGroup[];
}

const ShopByVehiclePage = ({ brandGroups }: ShopByVehiclePageProps) => {
  const gallery = galleryImages;

  return (
    <>
      <Header />
      <div className="header-gap-creator"></div>

      <main>
      <Breadcrumbs items={[{ label: 'Shop by Vehicle' }]} />
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Shop Engine Air Filters by Vehicle</HeroTitle>
          <HeroSubtitle>
            Find the right engine air filter for your car, truck, or motorcycle with Green Filter&rsquo;s vehicle
            finder. Select your year, make, model, and engine to see filters designed for your specific
            application, or search directly by part number.
          </HeroSubtitle>
          <HeroSubtitle>
            Green Filter manufactures washable, reusable performance air filters in the USA for drivers who want
            reliable vehicle-specific fitment and a long-term alternative to disposable engine air filters.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Filter Section */}
      <FilterSection id="vehicle-finder">
        <FilterContainer>
          <PerfectFilter />
        </FilterContainer>
        <TrustBar>Washable &amp; Reusable &middot; Made in the USA &middot; Limited Lifetime Warranty &middot; Vehicle-Specific Fitment</TrustBar>
      </FilterSection>

      <ContentWrapper>
        {/* Find the Right Air Filter */}
        <TextSection>
          <SectionHeading>Find the Right Air Filter for Your Vehicle</SectionHeading>
          <SectionText>The best engine air filter starts with the correct fit.</SectionText>
          <SectionText>
            Use the vehicle selector above to match your vehicle&rsquo;s year, make, model, and engine with
            compatible Green Filter applications.
          </SectionText>
          <SectionText>
            Whether you drive a daily commuter, pickup truck, performance car, motorcycle, or exotic vehicle,
            Green Filter makes it easier to find an air filter designed for your specific application.
          </SectionText>
          <SectionText>
            If you already have an OEM or competitor filter number, use our{' '}
            <InlineLink href="/cross-reference-filters">air filter cross-reference tool</InlineLink> to search for
            a compatible Green Filter.
          </SectionText>
          <SectionCTAWrapper>
            <SectionCTA href="#vehicle-finder">Select Your Vehicle and Find Your Filter</SectionCTA>
          </SectionCTAWrapper>
        </TextSection>

        {/* Why Choose a Green Filter */}
        <WhySection>
          <SectionHeadingCentered>Why Choose a Green Filter?</SectionHeadingCentered>
          <BenefitsGrid>
            {WHY_CHOOSE_ITEMS.map((item) => (
              <BenefitCard key={item.title}>
                <BenefitTitle>{item.title}</BenefitTitle>
                <BenefitText>{item.text}</BenefitText>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </WhySection>

        {/* Reusable Alternative */}
        <TextSectionCentered>
          <SectionHeadingCentered>A Reusable Alternative to Disposable Engine Air Filters</SectionHeadingCentered>
          <SectionText>
            Green Filter air filters are designed to be cleaned, serviced, and reused, providing a long-term
            alternative to disposable engine air filters. Use the vehicle finder above to check availability for
            your vehicle.
          </SectionText>
          <SectionCTAWrapper>
            <SectionCTA href="#vehicle-finder">Find My Air Filter</SectionCTA>
          </SectionCTAWrapper>
        </TextSectionCentered>

        <ShopElement1 brandGroups={brandGroups} />

        {/* How to Find the Correct Filter */}
        <TextSection>
          <SectionHeading>How to Find the Correct Engine Air Filter</SectionHeading>
          <SectionText>Finding the right Green Filter takes just a few steps:</SectionText>
          <StepsList>
            {HOW_TO_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </StepsList>
          <SectionText>
            If you already know the filter number you want to replace, search by part number or use the{' '}
            <InlineLink href="/cross-reference-filters">air filter cross-reference tool</InlineLink>.
          </SectionText>
          <SectionTextStrong>
            Correct fitment matters. Always confirm your exact vehicle and engine before purchasing.
          </SectionTextStrong>
          <SectionCTAWrapper>
            <SectionCTA href="#vehicle-finder">Find Matching Filters</SectionCTA>
          </SectionCTAWrapper>
        </TextSection>

        {/* Cleaning Guidance */}
        <TextSection>
          <SectionHeading>How Often Should You Clean a Reusable Engine Air Filter?</SectionHeading>
          <SectionText>
            Green Filter recommends inspecting your air filter during routine vehicle maintenance, including
            around oil-change intervals.
          </SectionText>
          <SectionText>
            Under normal driving conditions, cleaning is generally recommended around every 30,000 miles. Vehicles
            regularly driven in dusty, off-road, or unusually dirty environments may require more frequent
            inspection and cleaning.
          </SectionText>
          <SectionText>Check the filter sooner if you notice visible dirt or debris buildup.</SectionText>
          <SectionText>
            Maintenance needs can vary by vehicle and driving conditions, so always follow the recommended service
            instructions for your filter.
          </SectionText>
          <SectionText>
            For proper filter maintenance, shop Green Filter{' '}
            <InlineLink href="/cleaner-care">Cleaner &amp; Care products</InlineLink>.
          </SectionText>
        </TextSection>

        <FAQSection faqs={shopByVehicleFaqs} title="Frequently Asked Questions About Engine Air Filters" />
        <Gallery gallery={gallery} />

        {/* Final CTA */}
        <FinalCTASection>
          <SectionHeadingCentered>Find the Right Green Filter for Your Vehicle</SectionHeadingCentered>
          <FinalCTALead>Ready to find your filter?</FinalCTALead>
          <SectionText>
            Use the vehicle selector above to match your vehicle&rsquo;s year, make, model, and engine to see Green
            Filter applications designed for your vehicle.
          </SectionText>
          <SectionCTAWrapper>
            <SectionCTA href="#vehicle-finder">Find Matching Filters</SectionCTA>
          </SectionCTAWrapper>
          <TrustBar>Washable &amp; Reusable &middot; Made in the USA &middot; Limited Lifetime Warranty</TrustBar>
        </FinalCTASection>
      </ContentWrapper>
      </main>

      <Footer />
    </>
  );
};

export default ShopByVehiclePage;

// Styled Components

const HeroSection = styled.section`
  padding: 40px 20px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    padding: 50px 24px 35px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 40px 20px 30px;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin: 0 0 16px;
  line-height: 1.2;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    font-size: 40px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 32px;
    margin-bottom: 12px;
  }
`;

const HeroSubtitle = styled.p`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: ${COLORS.textSecondary};
  margin: 0 0 16px;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 16px;
  }
`;

const FilterSection = styled.section`
  background: linear-gradient(180deg, #ffffff 0%, ${COLORS.bgGray} 100%);
  padding: 40px 24px 32px;
  scroll-margin-top: 100px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 30px 16px 24px;
  }
`;

const FilterContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const TrustBar = styled.p`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  text-align: center;
  max-width: 800px;
  margin: 24px auto 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.xxs};
  }
`;

const ContentWrapper = styled.div`
  background: ${COLORS.bgGray};
  padding: 0;
`;

const TextSection = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: ${SPACING.sectionSmall} 20px;
  text-align: left;
`;

const TextSectionCentered = styled(TextSection)`
  text-align: center;
  background: ${COLORS.bgWhite};
`;

const SectionHeading = styled.h2`
  ${TYPOGRAPHY.h2}
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.md};
`;

const SectionHeadingCentered = styled(SectionHeading)`
  text-align: center;
`;

const SectionText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.gray800};
  margin: 0 0 ${SPACING.sm};
  line-height: 1.7;
`;

const SectionTextStrong = styled(SectionText)`
  font-weight: 700;
  color: ${COLORS.textPrimary};
`;

const InlineLink = styled.a`
  color: ${COLORS.brandGreenAccessible};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StepsList = styled.ol`
  ${TYPOGRAPHY.body}
  color: ${COLORS.gray800};
  margin: 0 0 ${SPACING.md};
  padding-left: 24px;
  line-height: 1.8;

  li {
    margin-bottom: 4px;
  }
`;

const SectionCTAWrapper = styled.div`
  margin-top: ${SPACING.lg};
`;

const SectionCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: ${COLORS.brandGreen};
  color: ${COLORS.white};
  ${TYPOGRAPHY.button}
  text-decoration: none;
  border-radius: 8px;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${COLORS.brandGreenHover};
    transform: translateY(-2px);
  }
`;

const WhySection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${SPACING.sectionSmall} 20px;
  background: ${COLORS.bgWhite};
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${SPACING.xl};
  margin-top: ${SPACING.xl};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
    gap: ${SPACING.lg};
  }
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: ${SPACING.xl};
  background: ${COLORS.bgGray};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const BenefitTitle = styled.h3`
  ${TYPOGRAPHY.h4}
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.sm};
`;

const BenefitText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;
`;

const FinalCTASection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${SPACING.sectionLarge} 20px;
  text-align: center;
`;

const FinalCTALead = styled.p`
  ${TYPOGRAPHY.bodyLG}
  font-weight: 600;
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.sm};
`;
